'use client'

import {
  ReactNode,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
} from 'react'
import { createPortal } from 'react-dom'

type TooltipProps = {
  content: string
  children: ReactNode
  position?: 'top' | 'bottom'
  className?: string
}

/** Inset from viewport edges when clamping tooltip position */
const EDGE = 8
const GAP = 8

export function Tooltip({ content, children, position = 'top', className }: TooltipProps) {
  const wrapRef = useRef<HTMLSpanElement>(null)
  const floatRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  const compute = useCallback(() => {
    const wrap = wrapRef.current
    const float = floatRef.current
    if (!wrap || !float || !open) return

    const trig = wrap.getBoundingClientRect()
    const tw = Math.max(float.offsetWidth, float.scrollWidth)
    const th = Math.max(float.offsetHeight, float.scrollHeight)
    const vh = window.innerHeight
    const vw = window.innerWidth

    const spaceAbove = trig.top - EDGE
    const spaceBelow = vh - trig.bottom - EDGE

    let placeAbove = position === 'top'
    if (placeAbove && spaceAbove < th + GAP && spaceBelow > spaceAbove) {
      placeAbove = false
    } else if (!placeAbove && spaceBelow < th + GAP && spaceAbove > spaceBelow) {
      placeAbove = true
    }

    let top = placeAbove ? trig.top - th - GAP : trig.bottom + GAP
    top = Math.min(Math.max(top, EDGE), Math.max(EDGE, vh - th - EDGE))

    let left = trig.left + trig.width / 2 - tw / 2
    left = Math.min(Math.max(left, EDGE), Math.max(EDGE, vw - tw - EDGE))

    setCoords({ top, left })
  }, [open, position])

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }

    const wrap = wrapRef.current
    const float = floatRef.current
    if (!wrap || !float) return

    compute()

    const ro = new ResizeObserver(compute)
    ro.observe(float)
    window.addEventListener('scroll', compute, true)
    window.addEventListener('resize', compute)

    return () => {
      ro.disconnect()
      window.removeEventListener('scroll', compute, true)
      window.removeEventListener('resize', compute)
    }
  }, [open, compute, content])

  function handleBlur(e: React.FocusEvent<HTMLSpanElement>) {
    const next = e.relatedTarget as Node | null
    if (next && wrapRef.current?.contains(next)) return
    setOpen(false)
  }

  const tip =
    mounted &&
    open &&
    createPortal(
      <div
        ref={floatRef}
        role="tooltip"
        className="pointer-events-none fixed z-[200] rounded-md bg-foreground px-2.5 py-1 text-left text-xs text-background shadow-lg max-w-[min(18rem,calc(100vw-16px))] whitespace-normal leading-snug"
        style={{
          top: coords?.top ?? 0,
          left: coords?.left ?? 0,
          visibility: coords ? 'visible' : 'hidden',
        }}
      >
        {content}
      </div>,
      document.body,
    )

  return (
    <>
      <span
        ref={wrapRef}
        className={['inline-flex items-center', className].filter(Boolean).join(' ')}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
      >
        {children}
      </span>
      {tip}
    </>
  )
}
